import type { ReactNode } from "react";
import { Eye, Layers, ShieldCheck } from "lucide-react";

import { Logo } from "@/components/shared/logo";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";

interface AuthSplitLayoutProps {
  children: ReactNode;
}

interface ValuePoint {
  numberLabel: string;
  Icon: typeof ShieldCheck;
  titleKey: TranslationKey;
  bodyKey: TranslationKey;
}

// Atrium capabilities — universal positioning, no sector lock.
// Icons selected to read clearly at 18px on the dark panel:
//   Eye          = visibility / observation
//   ShieldCheck  = policy / access enforcement
//   Layers       = multi-provider stack
const VALUE_POINTS: ReadonlyArray<ValuePoint> = [
  {
    numberLabel: "01",
    Icon: Eye,
    titleKey: "auth.brand.visibility.title",
    bodyKey: "auth.brand.visibility.body",
  },
  {
    numberLabel: "02",
    Icon: ShieldCheck,
    titleKey: "auth.brand.policy.title",
    bodyKey: "auth.brand.policy.body",
  },
  {
    numberLabel: "03",
    Icon: Layers,
    titleKey: "auth.brand.multiProvider.title",
    bodyKey: "auth.brand.multiProvider.body",
  },
];

/**
 * Two-column auth shell used by ``/login`` and ``/register``.
 *
 * Left column hosts the form (children); right column is the brand
 * panel — three Monago value points on a deep ocean gradient.
 *
 * Responsive treatment:
 *
 * * ``< desktop (1024px)``: brand panel HIDES, the form claims the
 *   full viewport. The first impression on a phone is task-focused
 *   (log in / register), not marketing. On tablet+ both columns
 *   render side-by-side.
 * * ``>= desktop``: 5:4 columns (form slightly narrower than panel)
 *   with the form column scrollable independently so the right
 *   panel stays put on long register wizards.
 *
 * Logic-free wrapper — auth flow (use-auth, api/auth, validation,
 * wizard steps) is untouched by this component.
 */
export function AuthSplitLayout({ children }: AuthSplitLayoutProps) {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white desktop:grid desktop:grid-cols-[minmax(0,5fr)_minmax(0,4fr)]">
      {/* ------------------------------------------------------------------
          Left — form column. White bg, sticky logo at top-left so the
          brand mark is present even when the form scrolls past it.
         ------------------------------------------------------------------ */}
      <div className="flex min-h-screen flex-col">
        <header className="px-6 pt-6 tablet:px-10 tablet:pt-8">
          <Logo size="md" />
        </header>
        <main className="flex flex-1 items-center justify-center px-6 py-10 tablet:px-10">
          <div className="w-full max-w-md">{children}</div>
        </main>
        <footer className="px-6 pb-6 text-xs text-neutral-500 tablet:px-10">
          {t("auth.brand.footerCopyright")}
        </footer>
      </div>

      {/* ------------------------------------------------------------------
          Right — brand panel. Deep ocean gradient with a subtle grid
          texture for atmosphere; numbered value points anchored on the
          left edge so the eye lands cleanly down the column.
         ------------------------------------------------------------------ */}
      <aside
        aria-label={t("auth.brand.panelAriaLabel")}
        className="relative hidden overflow-hidden bg-ocean-900 bg-gradient-to-br from-ocean-800 to-ocean-900 text-white desktop:flex desktop:flex-col"
      >
        {/* Decorative grid + radial highlight. Pointer-events-none so
            it never intercepts a stray click. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.08) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
            maskImage:
              "radial-gradient(ellipse 80% 60% at 30% 20%, black 40%, transparent 80%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 80% 60% at 30% 20%, black 40%, transparent 80%)",
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-32 top-1/3 h-96 w-96 rounded-full bg-ocean-400/15 blur-3xl"
        />

        <div className="relative flex flex-1 flex-col justify-between px-12 py-12 xl:px-16 xl:py-16">
          {/* Headline + tagline */}
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-ocean-200">
              {t("auth.brand.eyebrow")}
            </p>
            <h2 className="mt-5 text-4xl font-medium leading-tight text-white xl:text-5xl">
              {t("auth.brand.headline")}
            </h2>
            <p className="mt-4 max-w-md text-base leading-relaxed text-ocean-100/90 xl:text-lg">
              {t("auth.brand.subhead")}
            </p>
          </div>

          {/* Value points — numbered, divided. Anchored to the bottom
              so the headline + value points read top-to-bottom on the
              column even when the viewport is tall. */}
          <ol className="mt-12 space-y-px overflow-hidden rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur-sm">
            {VALUE_POINTS.map((point, index) => {
              const Icon = point.Icon;
              return (
                <li
                  key={point.numberLabel}
                  className={
                    index > 0
                      ? "border-t border-white/10 px-6 py-5 xl:px-8 xl:py-6"
                      : "px-6 py-5 xl:px-8 xl:py-6"
                  }
                >
                  <div className="flex items-start gap-4 xl:gap-6">
                    <div className="flex shrink-0 items-center gap-3">
                      <span className="font-mono text-xs font-medium tracking-wide text-ocean-200">
                        {point.numberLabel}
                      </span>
                      <span
                        aria-hidden="true"
                        className="flex h-10 w-10 items-center justify-center rounded-md bg-white/10 text-ocean-100"
                      >
                        <Icon size={18} strokeWidth={1.8} />
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base font-medium text-white">
                        {t(point.titleKey)}
                      </h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-ocean-100/80">
                        {t(point.bodyKey)}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </aside>
    </div>
  );
}
